import { FlexRow } from '@components'
/* eslint-disable max-lines-per-function */
import { type FC, useEffect, useState } from 'react'
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, type TooltipProps, XAxis, YAxis } from 'recharts'

import { Box, Text } from '@chakra-ui/react'

import { GraphTick } from '@/src/components/GraphTick'
import { numberFormat } from '@shared/utils'
import { appColors } from '@theme/foundations/colors'
import { format, parseISO, subMinutes } from 'date-fns'
import { de } from 'date-fns/locale'
import moment from 'moment'
import { getNiceTickValues } from 'recharts-scale'
import { LoadProfileDetailsGraphLegend } from './LoadProfileDetailsGraphLegend'
import { LoadProfileDetailsGraphTooltip } from './LoadProfileDetailsGraphTooltip'

interface ISimpleEmberLoadProfileGraph {
  data: { timestamp: string; value_kw: number }[]
  hideXAxis?: boolean
  hideDate?: boolean
}

const MAX_LABELS_PER_SCREEN_SIZE = [
  { threshold: 1920, max: 15, maxOneDay: 24 },
  { threshold: 1548, max: 15, maxOneDay: 24 },
  { threshold: 1412, max: 10, maxOneDay: 24 },
  { threshold: 1268, max: 6, maxOneDay: 24 },
  { threshold: 1203, max: 6, maxOneDay: 12 },
  { threshold: 1080, max: 5, maxOneDay: 12 },
  { threshold: 1014, max: 5, maxOneDay: 8 },
  { threshold: 949, max: 3, maxOneDay: 8 },
  { threshold: 945, max: 3, maxOneDay: 6 },
  { threshold: 901, max: 2, maxOneDay: 4 },
  { threshold: 890, max: 1, maxOneDay: 1 },
  { threshold: 0, max: 1, maxOneDay: 1 },
]

const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height,
  }
}

export const SimpleEmberLoadProfileGraph: FC<ISimpleEmberLoadProfileGraph> = ({ data, hideXAxis, hideDate }) => {
  const height = 300

  let days = 0
  days = Math.ceil(data.length / 96)
  const isOnlyOneDay = days === 1

  const chartViz = (
    <Line
      isAnimationActive={true}
      type='monotone'
      dataKey={'value_kw'}
      stroke={appColors.accentPrimary}
      strokeLinecap='round'
      strokeWidth={'3px'}
      dot={false}
      activeDot={true}
    />
  )

  const isQuarterly = true
  const unit = 'kW'
  const xAxisDataKey = 'timestamp'

  let currentMax = 0
  let niceTickValues: any = []

  if (data) {
    for (let i = data.length - 1; i >= 0; i--) {
      if (data[i].value_kw > currentMax) currentMax = data[i].value_kw
    }
    niceTickValues = getNiceTickValues([0, currentMax], 5, false)
  }

  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions())

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const maxLabels = MAX_LABELS_PER_SCREEN_SIZE.find(range => windowDimensions.width >= range.threshold)
  let xAxisInterval = isOnlyOneDay ? 3 : 96
  // @ts-ignore
  const factor = Math.ceil(days / maxLabels?.max)
  xAxisInterval = xAxisInterval * factor

  let firstDate
  let lastDate
  if (data) {
    firstDate = moment(data[0].timestamp)
    // @ts-ignore
    lastDate = moment(data.at(-1)?.timestamp)
  }

  return (
    <Box height={'100%'}>
      <Box height={'100%'}>
        <FlexRow alignItems='center' justifyContent={'space-between'}>
          <Text color='gray.400' fontSize={14} marginBottom={2}>
            {unit}
          </Text>
          <LoadProfileDetailsGraphLegend isQuarterly={true} isOnlyOneDay={isOnlyOneDay} />
        </FlexRow>

        <ResponsiveContainer width={'100%'} height={height}>
          <ComposedChart data={data}>
            {!hideXAxis && (
              <XAxis
                dataKey={xAxisDataKey}
                tickLine={false}
                tick={props => {
                  return (
                    <GraphTick
                      payload={props.payload}
                      x={props.x}
                      y={props.y}
                      rotate={isOnlyOneDay && isQuarterly}
                      showYear={!isOnlyOneDay}
                      formatter={value => {
                        return isOnlyOneDay && isQuarterly
                          ? format(subMinutes(parseISO(value), 15), 'HH:mm', { locale: de })
                          : format(parseISO(value), 'dd LLL', { locale: de })
                      }}
                    />
                  )
                }}
                tickMargin={isOnlyOneDay ? 6.4 : 12}
                axisLine={{ stroke: appColors.gray['300'] }}
                interval={xAxisInterval}
                padding={isOnlyOneDay ? { left: 12 } : undefined}
              />
            )}
            <YAxis
              type='number'
              domain={[0, niceTickValues.at(-1) || 0]}
              ticks={niceTickValues}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tick={{
                fontSize: '12px',
                fill: appColors.gray['400'],
              }}
              tickFormatter={value => numberFormat(value)}
              // scale={scale}
            />
            <Tooltip
              content={(tooltipProps: TooltipProps<number, string>) => (
                <LoadProfileDetailsGraphTooltip {...tooltipProps} isQuarterly={true} isOnlyOneDay={isOnlyOneDay} hideDate={hideDate} />
              )}
              wrapperStyle={{ outline: 'none' }}
            />
            {isOnlyOneDay && (
              <>
                <Area
                  type='monotone'
                  dataKey='high'
                  stroke={appColors.gray['200']}
                  fillOpacity={1}
                  fill={appColors.gray['200']}
                  // opacity={session.user?.companyId === '77lGK0n4ROQgDaSCJ7QN' ? 0 : undefined}
                />
                <Area
                  type='monotone'
                  dataKey='low'
                  stroke='#DEDEDE'
                  fillOpacity={1}
                  fill='white'
                  // opacity={session.user?.companyId === '77lGK0n4ROQgDaSCJ7QN' ? 0 : undefined}
                />
              </>
            )}{' '}
            <CartesianGrid vertical={false} stroke={'#F3F3F3'} />
            {chartViz}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}
